import { Fragment, useState } from "react";
import * as yup from "yup";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

import ButtonPrimary from "components/Buttons/ButtonPrimary";
import MyTextField from "components/Inputs/MyTextField";
import { httpUpdateMyUser } from "services/http/users";

const useStyles = makeStyles((_theme) => ({
  paper: { flexGrow: 1, height: "100%", overflowX: "auto", padding: 48 },
}));

const defaultValues = {
  old_password: "",
  new_password_1: "",
  new_password_2: "",
};

const Settings = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const classes = useStyles();

  const { myCompany } = props;

  const [values, setValues] = useState(defaultValues);
  const [errors, setErrors] = useState({ inValid: true });
  const [loading, setLoading] = useState(false);

  const validationSchema = yup.object().shape({
    new_password_2: yup
      .string()
      .required(t("Pakollinen kenttä"))
      .min(8, t("Salasanan on oltava vähintään 8 merkkiä"))
      .oneOf(
        [yup.ref("new_password_1"), null],
        t("Salasanan on täsmättävä uusi salasana kentän kanssa"),
      ),
    new_password_1: yup
      .string()
      .required(t("Pakollinen kenttä"))
      .min(8, t("Salasanan on oltava vähintään 8 merkkiä")),
    old_password: yup.string().required(t("Pakollinen kenttä")),
  });

  const updateValues = (updated_values) => {
    if (updated_values == null) {
      return;
    }

    setValues((prev) => ({ ...prev, ...updated_values }));
  };

  const handleFormChange = ({ target }) => {
    updateValues({ [target.name]: target.value });
  };

  const validateForm = () => {
    validationSchema
      .validate(values, { abortEarly: true })
      .then(() => {
        // GOOD
        setErrors({});
      })
      .catch((err) => {
        // INVALID
        setErrors({ [err.path]: t(err?.message), inValid: true });
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const data = {
      password: values?.new_password_1,
      old_password: values?.old_password,
    };

    setLoading(true);
    httpUpdateMyUser(data, myCompany?.id).then(
      (_response) => {
        enqueueSnackbar(t("Salasana vaihdettu"), {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        setValues(defaultValues);
        setLoading(false);
      },
      (errors) => {
        if (
          errors &&
          errors?.data &&
          errors?.data?.errorCode == "OLD_PASSWORD_WRONG"
        ) {
          enqueueSnackbar(
            t("Salasanan vaihto epäonnistui. Vanha salasana on väärin."),
            {
              variant: "error",
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
            },
          );
        } else {
          enqueueSnackbar(t("Salasanan vaihto epäonnistui"), {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
        }

        setLoading(false);
      },
    );
  };

  return (
    <Fragment>
      <Paper className={classes.paper} elevation={0} square>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4">{t("Asetukset")}</Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size="grow">
              <MyTextField
                autoFocus
                customLabel={t("Nykyinen salasana")}
                required
                id="settings_old_password"
                name="old_password"
                value={values.old_password || ""}
                onChange={handleFormChange}
                type="password"
                onBlur={validateForm}
                error={Boolean(errors?.old_password)}
                helperText={errors?.old_password}
              />

              <MyTextField
                customLabel={t("Uusi salasana")}
                required
                id="settings_new_password_1"
                name="new_password_1"
                value={values.new_password_1 || ""}
                onChange={handleFormChange}
                type="password"
                onBlur={validateForm}
                error={Boolean(errors?.new_password_1)}
                helperText={errors?.new_password_1}
              />

              <MyTextField
                customLabel={t("Uusi salasana uudelleen")}
                required
                id="settings_new_password_2"
                name="new_password_2"
                value={values.new_password_2 || ""}
                onChange={handleFormChange}
                type="password"
                onBlur={validateForm}
                error={Boolean(errors?.new_password_2)}
                helperText={errors?.new_password_2}
              />

              <ButtonPrimary type="submit" disabled={errors.inValid || loading}>
                {t("Tallenna salasana")}
              </ButtonPrimary>
            </Grid>

            <Grid size="grow"></Grid>
          </Grid>
        </form>
      </Paper>
    </Fragment>
  );
};

export default Settings;
